import {
  isShapeMirrorCopyEligible,
  isShapeMirrorEligible,
} from '@/lib/transforms/mirror-shape-across-spine'
import type { LiveSelection } from './live-selection'

type MirrorAvailabilityInput = {
  interactionLocked: boolean
  mirrorSpineAxisX: number | null | undefined
  hasShapeManager: boolean
  hasBatchCommit: boolean
}

export type MirrorAvailability = {
  mirrorMoveEligible: boolean
  mirrorCopyEligible: boolean
  mirrorMoveTooltip: string
  mirrorCopyTooltip: string
}

/**
 * Mirror Move/Copy enablement and tooltips from the live selection (matches canAlignMulti /
 * canSpaceSelection) instead of the activeObjects context — that context can go stale for some
 * selection paths while the manager's own selected-ids stay accurate, which was silently
 * disabling Mirror for a valid selection.
 */
export function resolveMirrorAvailability(
  { selectedShapes, allShapes }: LiveSelection,
  { interactionLocked, mirrorSpineAxisX, hasShapeManager, hasBatchCommit }: MirrorAvailabilityInput
): MirrorAvailability {
  const mirrorShapesById = new Map(allShapes.map((x) => [x.id, x]))
  const mirrorTotalSelectedCount = selectedShapes.length

  // Mirror Move repositions the existing object, so it's blocked by frozen (like any other
  // geometry gesture). Mirror Copy only ever creates a brand-new object and never touches the
  // original's placement, so a frozen source is still copyable — only locked blocks it, matching
  // how regular copy/paste already treats frozen objects. See `isShapeMirrorCopyEligible`.
  const mirrorMoveEligible =
    !interactionLocked &&
    mirrorSpineAxisX != null &&
    mirrorTotalSelectedCount > 0 &&
    hasShapeManager &&
    hasBatchCommit &&
    selectedShapes.every((s) => isShapeMirrorEligible(s, mirrorShapesById, []))

  const mirrorCopyEligible =
    !interactionLocked &&
    mirrorSpineAxisX != null &&
    mirrorTotalSelectedCount > 0 &&
    hasShapeManager &&
    selectedShapes.every((s) => isShapeMirrorCopyEligible(s, mirrorShapesById, []))

  const mirrorMoveTooltip =
    mirrorTotalSelectedCount === 0
      ? 'Select an object to use Mirror Move.'
      : mirrorMoveEligible
        ? 'Mirror Move'
        : 'Mirror Move isn’t available for the current selection.'

  const mirrorCopyTooltip =
    mirrorTotalSelectedCount === 0
      ? 'Select an object to use Mirror Copy.'
      : mirrorCopyEligible
        ? 'Mirror Copy'
        : 'Mirror Copy isn’t available for the current selection.'

  return { mirrorMoveEligible, mirrorCopyEligible, mirrorMoveTooltip, mirrorCopyTooltip }
}
